import { diff } from '@/utils'
import { on, trigger } from '@/hooks'
import Alpine from 'alpinejs'

/**
 * A commit represents an individual component updating itself server-side...
 */
export class Commit {
    constructor(component) {
        this.component = component
        this.isolate = false
        this.calls = []
        this.receivers = []
        this.resolvers = []
    }

    // Add a new resolver to be resolved when a commit is returned from the server...
    addResolver(resolver) {
        this.resolvers.push(resolver)
    }

    // Add a new action "call" to the commit payload...
    addCall(method, params, receiver) {
        this.calls.push({
            path: '', method, params,
            handleReturn(value) {
                receiver(value)
            },
        })
    }

    prepare() {
        trigger('commit.prepare', { component: this.component })
    }

    // The reason we are merging children into the encoded snapshot instead of the normal
    // snapshot is because we don't want to deal with the type issues that happen when
    // we deserialise and serialise the snapshot into JSON. So instead we merge the
    // children into the encoded snapshot manually by doing a string replace...
    getEncodedSnapshotWithLatestChildrenMergedIn() {
        let { snapshotEncoded, children, snapshot } = this.component
        let childIds = children.map(child => child.id)

        let filteredChildren = Object.fromEntries(
            Object.entries(snapshot.memo.children)
                .filter(([key, value]) => childIds.includes(value[1]))
        )

        return snapshotEncoded.replace(
            /"children":\{[^}]*\}/,
            `"children":${JSON.stringify(filteredChildren)}`
        )
    }

    // Generate a JSON-friendly server-request payload...
    toRequestPayload() {
        // Generate a "diff" of the current last known server-side state, and
        // the new front-end state so that we can update the server atomically...
        let propertiesDiff = diff(this.component.canonical, this.component.ephemeral)

        let updates = this.component.mergeQueuedUpdates(propertiesDiff)

        // Merge the component's current children into the encoded snapshot. That
        // way if any have been removed, they do not get sent to the server...
        let snapshotEncoded = this.getEncodedSnapshotWithLatestChildrenMergedIn()

        let payload = {
            snapshot: snapshotEncoded,
            updates: updates,
            calls: this.calls.map(i => ({
                path: i.path,
                method: i.method,
                params: i.params,
            }))
        }

        // Store success and failure hooks from commit listeners
        // so they can be aggregated into a singular callback later...
        let succeedCallbacks = []
        let failCallbacks = []
        let respondCallbacks = []

        let succeed = (fwd) => succeedCallbacks.forEach(i => i(fwd))
        let fail = () => failCallbacks.forEach(i => i())
        let respond = () => respondCallbacks.forEach(i => i())

        // Allow other areas of the codebase to hook into the lifecycle
        // of an individual commit...
        let finishTarget = trigger('commit', {
            component: this.component,
            commit: payload,
            succeed: (callback) => {
                succeedCallbacks.push(callback)
            },
            fail: (callback) => {
                failCallbacks.push(callback)
            },
            respond: (callback) => {
                respondCallbacks.push(callback)
            },
        })

        // Handle the response payload for a commit...
        let handleResponse = (response) => {
            let { snapshot, effects } = response

            respond()

            // Wrap in Alpine.transaction() to defer reactive effects until
            // after the morph completes. Without this, Alpine plugins like
            // x-mask can fire synthetic input events during the reactive
            // update, which trigger x-model setters that recreate deleted
            // array entries via dataSet() before the morph removes them...
            Alpine.transaction(() => {
                // Take the new snapshot and merge it into the existing one...
                this.component.mergeNewSnapshot(snapshot, effects, updates)

                // Trigger any side effects from the payload like "morph" and "dispatch event"...
                this.component.processEffects(this.component.effects)
            })

            if (effects['returns']) {
                let returns = effects['returns']

                // Here we'll match up returned values with their method call handlers. We need to build up
                // two "stacks" of the same length and walk through them together to handle them properly...
                let returnHandlerStack = this.calls.map(({ handleReturn }) => (handleReturn))

                returnHandlerStack.forEach((handleReturn, index) => {
                    handleReturn(returns[index])
                })
            }

            let parsedSnapshot = JSON.parse(snapshot)

            finishTarget({ snapshot: parsedSnapshot, effects })

            this.resolvers.forEach(i => i())

            succeed(response)
        }

        let handleFailure = () => {
            respond()

            fail()
        }

        return [payload, handleResponse, handleFailure]
    }
}
